/* eslint-disable react/prop-types */

import { debounce } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getReportData, getTotalCount } from '../../../../../api/reports';
import { Loader, Pagination, Table } from '../../../../../common/components';
import { useSetLoading } from '../../../../../common/hooks/appearance';
import { usePagination } from '../../../../../common/hooks/data';
import {
  fetchDashboardFilterData,
  reports,
  unpaidTransactionTableColumns,
} from '../../../../../utils/constant';
import * as handlers from '../../../../../utils/handlers';
import DownloadModal from '../../../downloadModal/downloadModal';
import EmailModal from '../../../emailModal/emailModal';
import ItemEmptyTile from '../../../item-empty-tile/item-empty-tile';
import Toolbar from '../../../toolbar/toolbar';
import onDownloadClick from '../../onDownloadClickHandler';
import UnpaidTransactionReportTableRow from './unpaidTransactionReportTableRow';

const UnpaidTransactionReportTable = ({ tableData, totalResults, fromDate, toDate }) => {
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  const [unpaidTransactionTableData, setUnpaidTransactionTableData] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const [slStartNumber, setSlStartNumber] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [initialRenderDone, setInitialRenderDone] = useState(false);

  useEffect(() => {
    if (!initialRenderDone) {
      setInitialRenderDone(true);
      return;
    }
    getUpdatedTableData('globalFilterChange');
  }, [filterData]);

  const toggleEmailModal = () => {
    if (unpaidTransactionTableData?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };

  const toggleDownloadModal = () => {
    if (unpaidTransactionTableData?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };

  useEffect(() => {
    setSlStartNumber(page * limit - limit);
    if (applyFilters) {
      getUpdatedTableData('pagination');
    }
  }, [page, limit, applyFilters]);

  const getUpdatedTableData = async (type = 'pagination', value = '') => {
    setLoading(true);
    setApplyFilters(false);

    let page2 = page;
    let limit2 = limit;
    if (type === 'search' || type === 'globalFilterChange') {
      page2 = 1;
      limit2 = 5;
    }
    try {
      const requestParams = {
        ...fetchDashboardFilterData(filterData),
        startDate: fromDate,
        endDate: toDate,
        page: page2,
        limit: limit2,
        timezone: moment.tz.guess(),
        ...((type === 'search' || searchText.length > 0) && {
          contractNumber: type === 'search' ? value : searchText,
        }),
      };
      const { data } = await getReportData(reports.UNPAID_TRANSACTION_REPORT, requestParams);
      if (type === 'search' || type === 'globalFilterChange') {
        setPage(1);
        setLimit(5);
        const response = await getTotalCount(reports.UNPAID_TRANSACTION_REPORT, {
          ...fetchDashboardFilterData(filterData),
          startDate: fromDate,
          endDate: toDate,
          contractNumber: type === 'search' ? value : searchText,
          timezone: moment.tz.guess(),
        });
        setTotalCount(response?.data?.data?.totalCount);
      }

      setUnpaidTransactionTableData(data?.data?.items || []);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      handlers.apiErrorHandler(error);
    }
  };

  const debounceApiCall = useCallback(
    debounce((val) => {
      getUpdatedTableData('search', val);
    }, 2000),
    [],
  );

  const onSerchInputChange = (event) => {
    const {
      target: { value },
    } = event;
    setSearchText(value);
    debounceApiCall(value);
  };

  useEffect(() => {
    setUnpaidTransactionTableData(tableData);
    setTotalCount(totalResults);
    setPage(1);
    setLimit(5);
    setSearchText('');
  }, [tableData, totalResults, setPage, setLimit]);

  const LoaderComponent = () => {
    if (loading) {
      return <Loader />;
    }
    return <ItemEmptyTile />;
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <Toolbar
        title="Unpaid Transaction"
        optionOneLabel="Email Report"
        optionOneIcon="MdOutlineEmail"
        onOptionOneClick={toggleEmailModal}
        optionTwoLabel="Download Report"
        optionTwoIcon="FiDownload"
        onOptionTwoClick={toggleDownloadModal}
        searchText={searchText}
        onSerchInputChange={onSerchInputChange}
        isSearchable
        SearchInputPlaceholder="Contract Number"
        isTableDataPresent={unpaidTransactionTableData?.length > 0}
      />
      {unpaidTransactionTableData && unpaidTransactionTableData?.length > 0 ? (
        <>
          <div className="table-resp-scroll">
            <Table
              headers={unpaidTransactionTableColumns}
              bordered
              responsive
              className="dashboard-table"
            >
              <tbody>
                {unpaidTransactionTableData?.map((rowData, rowNumber) => (
                  <UnpaidTransactionReportTableRow
                    key={slStartNumber + rowNumber + 1}
                    rowData={rowData}
                    id={slStartNumber + rowNumber + 1}
                  />
                ))}
              </tbody>
            </Table>
            {loading && <Loader />}
          </div>
          <div className="pagination-araging-report">
            <Pagination
              totalResults={totalCount}
              page={page}
              limit={limit}
              setPage={(pageNo) => {
                setApplyFilters(true);
                setPage(pageNo);
              }}
              setLimit={(rowlimit) => {
                setApplyFilters(true);
                setLimit(rowlimit);
              }}
            />
          </div>
        </>
      ) : (
        <div className="loader-empty">
          <LoaderComponent />
        </div>
      )}

      <EmailModal
        isOpen={isEmailModalOpen}
        toggle={toggleEmailModal}
        reportType={reports.UNPAID_TRANSACTION_REPORT}
        apiValues={{
          startDate: fromDate,
          endDate: toDate,
          ...(searchText && { contractNumber: searchText }),
        }}
        newFilters
      />
      <DownloadModal
        isOpen={isDownloadModalOpen}
        toggle={toggleDownloadModal}
        reportType={reports.UNPAID_TRANSACTION_REPORT}
        urlParams={{
          startDate: fromDate,
          endDate: toDate,
          action: 'download',
          ...(searchText && { contractNumber: searchText }),
        }}
        onDownloadClick={onDownloadClick}
        willReceiveReportFileInEmail
        newFilters
      />
    </>
  );
};

export default UnpaidTransactionReportTable;
